/// <reference types="vite/client" />
// Wardyn Web API client — JWT-based, mirrors mobile client shape

// All calls go to the CF Worker REST API

const API_URL = import.meta.env.VITE_API_URL ?? 'https://wardyn-cloud.musaabdulkabeer19.workers.dev/api';

// ─── Token store (localStorage) ──────────────────────────────────────────────

const STORAGE_KEYS = {
  accessToken:  'wardyn_access_token',
  refreshToken: 'wardyn_refresh_token',
  userId:       'wardyn_user_id',
  email:        'wardyn_email',
  deviceId:     'wardyn_device_id',
};

export function getStoredTokens() {
  return {
    accessToken:  localStorage.getItem(STORAGE_KEYS.accessToken),
    refreshToken: localStorage.getItem(STORAGE_KEYS.refreshToken),
    userId:       localStorage.getItem(STORAGE_KEYS.userId),
    email:        localStorage.getItem(STORAGE_KEYS.email),
    deviceId:     localStorage.getItem(STORAGE_KEYS.deviceId),
  };
}

export function storeTokens(tokens: {
  accessToken: string; refreshToken: string; userId?: string; email?: string;
}) {
  localStorage.setItem(STORAGE_KEYS.accessToken,  tokens.accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refreshToken);
  if (tokens.userId) localStorage.setItem(STORAGE_KEYS.userId, tokens.userId);
  if (tokens.email)  localStorage.setItem(STORAGE_KEYS.email,  tokens.email);
}

export function clearTokens() {
  Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
}

export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(STORAGE_KEYS.deviceId);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(STORAGE_KEYS.deviceId, id);
  }
  return id;
}

// ─── HTTP client ─────────────────────────────────────────────────────────────

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) return null;

  const res = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken, deviceId: getOrCreateDeviceId() }),
  });
  if (!res.ok) { clearTokens(); return null; }

  const data = await res.json() as { accessToken: string; refreshToken: string };
  storeTokens(data);
  return data.accessToken;
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { accessToken } = getStoredTokens();

  const doFetch = (token: string | null) => fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  let res = await doFetch(accessToken);

  // 401 → try refresh once
  if (res.status === 401) {
    if (isRefreshing) {
      // Queue this request until refresh completes
      const newToken = await new Promise<string>((resolve) => refreshQueue.push(resolve));
      res = await doFetch(newToken);
    } else {
      isRefreshing = true;
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      refreshQueue.forEach((cb) => cb(newToken ?? ''));
      refreshQueue = [];
      if (!newToken) throw new Error('Session expired — please log in again');
      res = await doFetch(newToken);
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function register(email: string, password: string) {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, deviceId: getOrCreateDeviceId() }),
  });
  if (!res.ok) {
    const e = await res.json() as { error?: string };
    throw new Error(e.error ?? 'Registration failed');
  }
  const data = await res.json() as { accessToken: string; refreshToken: string; userId: string };
  storeTokens({ ...data, email });
  return data;
}

export async function login(email: string, password: string) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, deviceId: getOrCreateDeviceId() }),
  });
  if (!res.ok) {
    const e = await res.json() as { error?: string };
    throw new Error(e.error ?? 'Login failed');
  }
  const data = await res.json() as { accessToken: string; refreshToken: string; userId: string };
  storeTokens({ ...data, email });
  return data;
}

export async function logout() {
  const { refreshToken } = getStoredTokens();
  if (refreshToken) {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {});
  }
  clearTokens();
}

// ─── Data ─────────────────────────────────────────────────────────────────────

export const api = {
  digest:    (type = 'morning') => apiRequest<DigestResponse>(`/digest?type=${type}&deviceId=${getOrCreateDeviceId()}`),
  tasks:     (status = 'all')   => apiRequest<{ tasks: Task[] }>(`/tasks?status=${status}`).then(r => r.tasks),
  emails:    ()                  => apiRequest<{ emails: Email[] }>('/emails').then(r => r.emails),
  linkedin:  ()                  => apiRequest<{ items: LinkedInItem[] }>('/linkedin').then(r => r.items),
  poll:      (since: number)     => apiRequest<PollResponse>(`/poll?since=${since}`),

  updateTask:    (id: string, update: { status?: string; priority?: string }) =>
    apiRequest(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(update) }),
  createTask:    (task: { title: string; priority?: string; dueAt?: number }) =>
    apiRequest('/tasks', { method: 'POST', body: JSON.stringify(task) }),
  triageEmail:   (id: string, action: string) =>
    apiRequest(`/emails/${id}`, { method: 'PATCH', body: JSON.stringify({ action }) }),
  handleLinkedIn: (id: string) =>
    apiRequest(`/linkedin/${id}`, { method: 'PATCH', body: JSON.stringify({ handled: true }) }),
};

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface Task {
  id: string; title: string; description?: string;
  priority: 'urgent'|'high'|'medium'|'low'; status: 'pending'|'in_progress'|'done'|'skipped';
  source: string; dueAt?: number; completedAt?: number; createdAt: number; updatedAt: number;
  ai_summary?: string; ai_action?: string;
}

export interface Email {
  id: string; subject: string; sender: string; snippet?: string;
  priority: 'urgent'|'high'|'normal'|'low'; isTriaged: boolean; receivedAt?: number;
  ai_summary?: string; ai_action?: string; ai_sentiment?: string;
}

export interface CalendarEvent {
  id: string; title: string; startAt: number; endAt: number;
  isAllDay: boolean; location?: string; attendeeCount: number;
}

export interface LinkedInItem {
  id: string; type: string; fromName?: string; fromTitle?: string;
  snippet?: string; isHandled: boolean; ai_summary?: string; ai_sentiment?: string;
}

export interface DigestResponse {
  type: string; generatedAt: number;
  tasks: { urgent: Task[]; dueToday: Task[]; overdue: Task[]; completedToday: number; totalPending: number };
  emails: { urgent: Email[]; untriagedCount: number };
  calendar: { today: CalendarEvent[]; upcoming: CalendarEvent[] };
  linkedin: { pendingCount: number; items: LinkedInItem[] };
}

export interface PollResponse {
  ok: boolean; hasChanges: boolean; polledAt: number;
  delta: { tasks: Partial<Task>[]; emails: Partial<Email>[]; linkedin: Partial<LinkedInItem>[]; calendar: Partial<CalendarEvent>[] };
}
